import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getMaterialBySlug } from '../../services/materialService';
import usePageMetadata from '../../hooks/usePageMetadata';
import MaterialDetail from './MaterialDetail';
import { getMaterialMetadata } from '../../shared/materialPresentation.mjs';
import { readInitialMaterial } from './initialMaterial';

const MaterialDetailPage = () => {
    const { materialSlug } = useParams();
    const [result, setResult] = useState(() => readInitialMaterial(materialSlug));
    const initialResult = useRef(result);
    const current = result?.slug === materialSlug ? result : null;
    const material = current?.material;
    const metadata = getMaterialMetadata(material);
    usePageMetadata(metadata.title, metadata.description, metadata);

    useLayoutEffect(() => {
        // Runs only after equivalent React content commits, before the next paint.
        if (current?.material || current?.error) {
            document.getElementById('material-initial-content')?.remove();
            document.getElementById('material-initial-data')?.remove();
        }
    }, [current]);

    useEffect(() => {
        // Retain the first snapshot through StrictMode's effect replay, but never
        // reuse it after navigating to another slug (including a later return).
        if (initialResult.current?.slug === materialSlug) return;
        initialResult.current = null;
        let active = true;
        setResult(null);
        getMaterialBySlug(materialSlug).then(material => {
            if (active) setResult({ slug: materialSlug, material, error: material ? null : 'Material not found.' });
        }).catch(error => {
            if (active) setResult({
                slug: materialSlug,
                error: error.response?.status === 404 ? 'Material not found.' : 'Unable to load material. Please try again later.'
            });
        });
        return () => { active = false; };
    }, [materialSlug]);

    if (!current) return <p role="status">Loading material...</p>;
    if (current.error) return (
        <div className="material-detail">
            <h1>Material details</h1>
            <p role="alert">{current.error}</p>
            <Link to="/materials/listofmaterials">Back to material list</Link>
        </div>
    );
    return <MaterialDetail material={material} needsEditFetch={current.fromDocument} />;
};

export default MaterialDetailPage;
